import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db, storage } from './firebase'; // Assuming firebase.js is in src
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import './SharePage.css'; // New dedicated CSS file
import { onAuthStateChanged } from "firebase/auth";
import { auth, functions } from './firebase'; // Make sure auth is imported
import { httpsCallable } from "firebase/functions";
import { usePostHog } from 'posthog-js/react';
import CreditsCounter from './components/CreditsCounter';
import PromoCard from './components/PromoCard';
import { useAdminView } from './contexts/AdminViewContext'; // Use admin view context
import stravaConnectButton from './assets/strava/btn_strava_connect_with_orange.svg';
import MapBadges from './components/MapBadges';

// Helper to format duration from seconds
const formatDuration = (totalSeconds) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (hours > 0) {
        return `${hours}h ${minutes}min`;
    } else if (minutes > 0) {
        return `${minutes} min`;
    } else {
        return `${seconds} sec`;
    }
};

// Helper for activity icons
const ActivityIcon = ({ type }) => {
    // Return nothing - we're removing the icon
    return null;
};

// Fun loading messages for AI processing
const aiProcessingMessages = [
    // Use i18n keys to allow localization of the rotating messages
    'share.aiMsgs.unrolling',
    'share.aiMsgs.charting',
    'share.aiMsgs.summoning',
    'share.aiMsgs.inkingCompass',
    'share.aiMsgs.aligning',
    'share.aiMsgs.aging',
    'share.aiMsgs.consultingStars',
    'share.aiMsgs.burnishing',
    'share.aiMsgs.tracing',
    'share.aiMsgs.annotating',
    'share.aiMsgs.surveying',
    'share.aiMsgs.tome',
    'share.aiMsgs.binding',
    'share.aiMsgs.polishing',
    'share.aiMsgs.mappingEdge',
    'share.aiMsgs.restoring',
    'share.aiMsgs.settingContours',
    'share.aiMsgs.brewingWeather',
    'share.aiMsgs.compilingHeraldic',
    'share.aiMsgs.monastic',
    'share.aiMsgs.distillingWind',
    'share.aiMsgs.conjuringMountains',
    'share.aiMsgs.imbuingRoads',
    'share.aiMsgs.stitchingRealms',
    'share.aiMsgs.flatteningPlateaus',
    'share.aiMsgs.plottingValleys',
    'share.aiMsgs.paintingCoasts',
    'share.aiMsgs.markingPirates',
    'share.aiMsgs.engravingElven',
    'share.aiMsgs.linkingWaypoints',
    'share.aiMsgs.adjustingWyvern',
    'share.aiMsgs.translatingCiphers',
    'share.aiMsgs.embeddingRoutes',
    'share.aiMsgs.unveilingInfra',
    'share.aiMsgs.reconstructingBorders',
    'share.aiMsgs.forgingSeals',
    'share.aiMsgs.indexingElevation',
    'share.aiMsgs.infusingElemental',
    'share.aiMsgs.scribingBorders',
    'share.aiMsgs.calibratingTides',
    'share.aiMsgs.erodingPassages',
    'share.aiMsgs.encodingMargins',
    'share.aiMsgs.resolvingCompass',
    'share.aiMsgs.curingInk',
    'share.aiMsgs.rebindingCodex',
    'share.aiMsgs.temperingGlyph',
    'share.aiMsgs.harvestingDust',
    'share.aiMsgs.reweavingWeb',
    'share.aiMsgs.anchoringReality',
    'share.aiMsgs.harmonizingLegends',
    'share.aiMsgs.inspectingSigils',
    'share.aiMsgs.finalizingBlessing'
  ];
  

// Helper to get user-friendly status message
const getStatusMessage = (status, aiMessage = null, t) => {
    switch (status) {
        case 'initiating_gpx_download':
        case 'fetching_activity_details':
            return t('share.fetching', 'Fetching your activity data from Strava...');
        case 'gpx_downloading_authenticated':
        case 'gpx_downloading_unauthenticated':
            return t('share.downloadingRoute', 'Downloading your activity route...');
        case 'geodata_created':
            return t('share.processingRoute', 'Processing your route data...');
        case 'mapbox_generating':
            return t('share.creatingVisualization', 'Creating your map visualization...');
        case 'mapbox_generated':
            return t('share.generatingTransformation', 'Generating your mythic transformation...');
        case 'openai_processing':
            return aiMessage || t('share.aiWorking', 'AI is working its magic on your map...');
        case 'openai_processing_complete':
            return t('share.ready', 'Your mythic map is ready!');
        default:
            if (status && (status.includes('error') || status.includes('failed'))) {
                return t('share.errorGeneric', 'Something went wrong during generation. Please try again.');
            }
            return t('share.processing', 'Processing your mythic map...');
    }
};

function SharePage() {
    const { t } = useTranslation();
    const { requestId } = useParams();
    const [requestData, setRequestData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null); // Add user state

    const [originalImageSrc, setOriginalImageSrc] = useState('');
    const [generatedImageSrc, setGeneratedImageSrc] = useState('');
    const [imageDisplayVisible, setImageDisplayVisible] = useState(false);
    const [sliderValue, setSliderValue] = useState(50);
    const [isDownloading, setIsDownloading] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [shareInstructions, setShareInstructions] = useState('');
    const [isUploadingComposite, setIsUploadingComposite] = useState(false);
    
    // AI processing message cycling
    const [currentAiMessage, setCurrentAiMessage] = useState('');
    
    // Progress tracking
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
    const [averageGenerationTime, setAverageGenerationTime] = useState(120); // Default 2 minutes
    const [generationStartTime, setGenerationStartTime] = useState(null);

    const imageComparisonDivRef = useRef(null);
    const isDraggingRef = useRef(false);
    // No animation on share page for simplicity, can be added if desired

    const posthog = usePostHog();
    
    const navigate = useNavigate();
    
    // Helper function to show custom dialog instead of alert
    const showCustomDialog = (type, title, message, icon = '') => {
        posthog?.capture('dialog_shown', {
            dialog_type: type,
            dialog_title: title,
            request_id: requestId,
            page: 'share'
        });
        
        setDialogContent({ type, title, message, icon });
        setShowDialog(true);
    };

    // Helper function to check if map was generated before watermark implementation
    const isLegacyMap = () => {
        if (!requestData) return false;
        
        // Watermark feature was implemented on June 26, 2025
        const watermarkImplementationDate = new Date('2025-06-26T00:00:00Z');
        
        // Check various possible date fields
        const generationDate = 
            requestData.createdAt?.toDate?.() ||
            requestData.timestamp?.toDate?.() ||
            requestData.generatedAt?.toDate?.() ||
            requestData.generationStartTime?.toDate?.() ||
            (requestData.createdAt instanceof Date ? requestData.createdAt : null) ||
            (requestData.timestamp instanceof Date ? requestData.timestamp : null) ||
            (requestData.generatedAt instanceof Date ? requestData.generatedAt : null) ||
            (requestData.generationStartTime instanceof Date ? requestData.generationStartTime : null);
        
        if (!generationDate) return false;
        
        return generationDate < watermarkImplementationDate;
    };

    // Add monthly usage state
    const [monthlyUsage, setMonthlyUsage] = useState({ currentCount: 0, monthlyLimit: 10, remaining: 10 });
    const [isLoadingUsage, setIsLoadingUsage] = useState(true);

    const [generatedImageBlob, setGeneratedImageBlob] = useState(null);
    const [contourImageUrl, setContourImageUrl] = useState(null);
    const [contourPrompt, setContourPrompt] = useState('');
    const [isContourOverlayOpen, setIsContourOverlayOpen] = useState(false);

    // Watermark check state
    const [isCheckingWatermark, setIsCheckingWatermark] = useState(false);
    const [hasExistingWatermark, setHasExistingWatermark] = useState(false);
    const [showWatermarkButton, setShowWatermarkButton] = useState(false);
    const [watermarkCheckStatus, setWatermarkCheckStatus] = useState(null);
    const [watermarkCheckError, setWatermarkCheckError] = useState(null);
    const checkInProgressRef = useRef(false);
    
    // Constants for watermark sharing
    const WATERMARK_SHARE_PENDING_KEY = 'watermark_share_pending';
    
    // Generate a unique session ID for this page load
    const sessionIdRef = useRef(Date.now() + '_' + Math.random().toString(36).substr(2, 9));
    const contourDetailsFetchedRef = useRef(false);
    
    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({
        type: 'success', // 'success', 'error', 'info'
        title: '',
        message: '',
        icon: ''
    });
    const [showSocialShareModal, setShowSocialShareModal] = useState(false);
    const [stravaShareInitiated, setStravaShareInitiated] = useState(false);
    
    // Admin state from context
    const { isAdmin } = useAdminView();
    
    // Feedback state
    const [userFeedback, setUserFeedback] = useState(null); // 'like' or 'dislike'
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    
    // Activity watermark credit state
    const [activityHasClaimedCredit, setActivityHasClaimedCredit] = useState(false);
    const [isCheckingActivityCredit, setIsCheckingActivityCredit] = useState(false);
    
    // Gallery visibility state
    const [isPublic, setIsPublic] = useState(false);
    const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
    
    // Regeneration state
    const [regenerationInfo, setRegenerationInfo] = useState({
        isRegeneration: false,
        regenerationNumber: 0,
        remainingRegenerations: 3
    });
    
    // Badge state
    const [selectedBadges, setSelectedBadges] = useState([]);
    const [badgePositions, setBadgePositions] = useState({});
    const [badgeSizes, setBadgeSizes] = useState({});
    const [draggingBadge, setDraggingBadge] = useState(null);
    const [selectedBadgeId, setSelectedBadgeId] = useState(null);
    const [hoveredBadgeId, setHoveredBadgeId] = useState(null);
    const [sliderPosition, setSliderPosition] = useState({ top: 0, left: 0 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const [availableBadges, setAvailableBadges] = useState([]);
    const [isLoadingBadges, setIsLoadingBadges] = useState(false);
    const dragOccurredRef = useRef(false);
    const [hasLoadedBadges, setHasLoadedBadges] = useState(false); // Track if badges were loaded from saved data
    const [badgesVisibleToAll, setBadgesVisibleToAll] = useState(false);

    // Track whether we've already attempted an early fetch for this request
    const badgesEarlyFetchedRef = useRef(false);

    // Control instructions visibility for final page
    const [showBadgeInstructions, setShowBadgeInstructions] = useState(true);

    const updateClipPath = (percentage) => {
        const generatedImg = document.getElementById('generatedImageDisplayShared'); // Unique ID for this page
        if (generatedImg) {
            generatedImg.style.clipPath = `polygon(0 0, 100% 0, 100% ${percentage}%, 0 ${percentage}%)`;
        }
    };

    // Fetch generation time statistics
    const fetchGenerationStats = async () => {
        try {
            //console.log('[SharePage] Fetching generation time stats...');
            const getGenerationTimeStats = httpsCallable(functions, 'getGenerationTimeStats');
            const result = await getGenerationTimeStats();
            const stats = result.data;
            //console.log('[SharePage] Generation stats received:', stats);
            //console.log('[SharePage] Average duration from backend:', stats.averageDurationSeconds);
            //console.log('[SharePage] Sample size:', stats.sampleSize);
            //console.log('[SharePage] Last updated:', stats.lastUpdated);
            
            const newAverageTime = stats.averageDurationSeconds || 120;
            //console.log('[SharePage] Setting average generation time to:', newAverageTime);
            setAverageGenerationTime(newAverageTime);
        } catch (error) {
            //console.error('[SharePage] Error fetching generation stats:', error);
            //console.log('[SharePage] Using default average generation time: 120 seconds');
            // Keep default value
        }
    };

    // Fetch model-specific generation time statistics
    const fetchModelSpecificStats = async (model) => {
        try {
            console.log('[SharePage] Fetching model-specific stats for:', model);
            const getGenerationTimeStatsByModel = httpsCallable(functions, 'getGenerationTimeStatsByModel');
            const result = await getGenerationTimeStatsByModel({ model });
            const stats = result.data;
            console.log('[SharePage] Model-specific stats received:', stats);
            
            if (stats[model]) {
                const modelAverageTime = stats[model].averageDurationSeconds || 120;
                console.log('[SharePage] Model average duration:', modelAverageTime);
                console.log('[SharePage] Model sample size:', stats[model].sampleSize);
                setAverageGenerationTime(modelAverageTime);
            } else {
                console.log('[SharePage] No stats for model, using default');
                await fetchGenerationStats(); // Fall back to overall stats
            }
        } catch (error) {
            console.error('[SharePage] Error fetching model-specific stats:', error);
            await fetchGenerationStats(); // Fall back to overall stats
        }
    };

    // Calculate progress based on elapsed time and status
    const calculateProgress = (status, startTime, currentTime) => {
        if (!startTime || !currentTime) return 0;
        
        const elapsedSeconds = (currentTime - startTime.toDate().getTime()) / 1000;
        const progressRatio = Math.min(elapsedSeconds / averageGenerationTime, 0.95); // Cap at 95% until complete
        
        // Adjust progress based on status
        let statusProgress = 0;
        switch (status) {
            case 'fetching_activity_details':
            case 'initiating_gpx_download':
                statusProgress = 0.02;
                break;
            case 'gpx_downloading_authenticated':
            case 'gpx_downloading_unauthenticated':
                statusProgress = 0.04;
                break;
            case 'geodata_created':
                statusProgress = 0.06;
                break;
            case 'mapbox_generating':
                statusProgress = 0.08;
                break;
            case 'mapbox_generated':
                statusProgress = 0.10;
                break;
            case 'ready_for_openai_processing':
                statusProgress = 0.12;
                break;
            case 'openai_processing':
                statusProgress = 0.15; // AI processing is the longest step, so keep it low
                break;
            case 'openai_processing_complete':
                return 100;
            default:
                statusProgress = 0;
        }
        
        // Use the higher of time-based or status-based progress
        return Math.max(statusProgress * 100, progressRatio * 100);
    };

    // Calculate estimated time remaining
    const calculateETA = (progress, startTime, currentTime) => {
        if (!startTime || !currentTime || progress >= 100) return null;
        
        const elapsedSeconds = (currentTime - startTime.toDate().getTime()) / 1000;
        
        // console.log('[SharePage] ETA Calculation:', {
        //     progress: progress,
        //     elapsedSeconds: elapsedSeconds,
        //     averageGenerationTime: averageGenerationTime
        // });
        
        // Always use average generation time for consistent ETA
        const remainingTime = Math.max(0, averageGenerationTime - elapsedSeconds);
        
        // console.log('[SharePage] Using average time method:', {
        //     averageGenerationTime: averageGenerationTime,
        //     elapsedSeconds: elapsedSeconds,
        //     remainingTime: remainingTime
        // });
        
        // Don't show ETA if remaining time is less than 5 seconds
        if (remainingTime < 5) {
            //console.log('[SharePage] ETA too small, returning null');
            return null;
        }
        
        // Add 10% buffer to make estimates more conservative
        const bufferedETA = Math.round(remainingTime * 1.1);
        //console.log('[SharePage] Final ETA (with 10% buffer):', bufferedETA);
        return bufferedETA;
    };

    // Fetch generation stats on mount
    useEffect(() => {
        fetchGenerationStats();
    }, []);

    useEffect(() => {
        contourDetailsFetchedRef.current = false;
        setContourImageUrl(null);
        setContourPrompt('');
        setIsContourOverlayOpen(false);
    }, [requestId]);

    useEffect(() => {
        setIsContourOverlayOpen(false);
    }, [contourImageUrl]);

    // Fetch badge visibility setting
    useEffect(() => {
        const fetchBadgeVisibility = async () => {
            try {
                const settingsDoc = await getDoc(doc(db, 'settings', 'mapGeneration'));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    setBadgesVisibleToAll(data.badgesVisibleToAll || false);
                    console.log('[SharePage] Badge visibility setting:', data.badgesVisibleToAll);
                }
            } catch (error) {
                console.error('[SharePage] Error fetching badge visibility:', error);
            }
        };
        fetchBadgeVisibility();
    }, []);

    // Function to load monthly usage
    const loadMonthlyUsage = async (user) => {
        if (!user) {
            setIsLoadingUsage(false);
            return;
        }
        
        setIsLoadingUsage(true);
        try {
            const { httpsCallable } = await import("firebase/functions");
            const { functions } = await import("./firebase");
            const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
            const result = await getUserMonthlyUsageFunc();
            setMonthlyUsage(result.data);
        } catch (error) {
            console.error("Error loading monthly usage:", error);
            // Keep default values on error
        } finally {
            setIsLoadingUsage(false);
        }
    };

    // Function to fetch available badges based on generation request ID
    const fetchAvailableBadges = async (generationRequestId) => {
        console.log('[fetchAvailableBadges] Called with requestId:', generationRequestId);
        console.log('[fetchAvailableBadges] Current user:', currentUser?.uid);
        
        if (!currentUser || !generationRequestId) {
            console.log('[fetchAvailableBadges] No current user or requestId, returning');
            return;
        }
        
        setIsLoadingBadges(true);
        try {
            console.log('[fetchAvailableBadges] Calling getUserBadges function');
            
            // Call backend function to get valid badges
            const getUserBadges = httpsCallable(functions, 'getUserBadges');
            const result = await getUserBadges({ generationRequestId });
            
            const badges = result.data.badges || [];
            console.log('[fetchAvailableBadges] Received valid badges from backend:', badges);
            setAvailableBadges(badges);
        } catch (error) {
            console.error('[fetchAvailableBadges] Error:', error);
            setAvailableBadges([]);
        } finally {
            setIsLoadingBadges(false);
        }
    };

    // Add auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            console.log('[SharePage] Auth state changed:', user?.uid);
            setCurrentUser(user);
            loadMonthlyUsage(user);
            
            // If we have request data with activity info and images are loaded, fetch badges (admin or if visible to all)
            if (user && requestData && imageDisplayVisible && requestId && (isAdmin || badgesVisibleToAll)) {
                console.log('[SharePage] User authenticated and images ready, checking for badges');
                console.log('[SharePage] Fetching badges for request:', requestId, {
                    isAdmin,
                    badgesVisibleToAll
                });
                fetchAvailableBadges(requestId);
            }
        });
        return () => unsubscribe();
    }, [requestData, imageDisplayVisible, requestId, isAdmin, badgesVisibleToAll]);

    // Fetch badges when currentUser becomes available (handles race condition where
    // styleName is detected before auth state is loaded)
    useEffect(() => {
        if (
            currentUser &&
            requestId &&
            requestData?.styleName &&
            !badgesEarlyFetchedRef.current &&
            availableBadges.length === 0 &&
            !isLoadingBadges &&
            (isAdmin || badgesVisibleToAll)
        ) {
            console.log('[SharePage] currentUser now available, fetching badges for styleName:', requestData.styleName);
            badgesEarlyFetchedRef.current = true;
            fetchAvailableBadges(requestId);
        }
    }, [currentUser, requestId, requestData?.styleName, isAdmin, badgesVisibleToAll, availableBadges.length, isLoadingBadges]);

    // Function to run watermark check
    const runWatermarkCheck = async (isAutoCheck = false, isInitialAutoCheck = false) => {
        // Prevent concurrent checks
        if (checkInProgressRef.current) {
            console.log('Watermark check already in progress, skipping...');
            return;
        }
        
        if (!requestId || !requestData?.StravaId || !currentUser) {
            if (!isAutoCheck) {
                showCustomDialog(
                    'info',
                    'Authentication Required',
                    'Please sign in and ensure the map is linked to a Strava activity.',
                    'ti-info-circle'
                );
            }
            return;
        }
        
        checkInProgressRef.current = true;
        setIsCheckingWatermark(true);
        
        try {
            const checkWatermarksFunc = httpsCallable(functions, 'checkActivityWatermarks');
            const result = await checkWatermarksFunc({ requestId: requestId });
            
            setHasExistingWatermark(result.data.summary?.hasWatermark || false);
            
            if (result.data.summary?.hasWatermark) {
                // Always show success dialog for found watermarks
                showCustomDialog(
                    'success',
                    t('ios.foundTitle'),
                    t('ios.foundMessage'),
                    'ti-check-circle'
                );
                
                // Update activity credit status since credit was awarded
                setActivityHasClaimedCredit(true);
                // Refresh monthly usage
                loadMonthlyUsage(currentUser);
                // Clear pending state on success
                localStorage.removeItem(WATERMARK_SHARE_PENDING_KEY);
                
                // Track PostHog event
                posthog?.capture('claimed_strava_share_credit', {
                    request_id: requestId,
                    strava_activity_id: requestData?.StravaId,
                    timestamp: new Date().toISOString()
                });
            } else {
                // Only show "not found" dialog if this is NOT the initial auto-check
                if (!isAutoCheck || !isInitialAutoCheck) {
                    showCustomDialog(
                        'info',
                        t('ios.notFoundTitle'),
                        <>
                            {t('ios.notFoundMessage')}{' '}
                            <a href="https://discord.gg/fprRUxfyu4" target="_blank" rel="noopener noreferrer" style={{ color: '#5865F2', textDecoration: 'underline' }}>
                                Discord
                            </a>
                        </>,
                        'ti-search'
                    );
                }
            }
            
            posthog?.capture('watermark_check_run', {
                request_id: requestId,
                activity_id: requestData.StravaId,
                watermark_found: result.data.summary?.hasWatermark || false,
                highest_confidence: result.data.summary?.highestWatermarkConfidence || 0,
                is_auto_check: isAutoCheck
            });
        } catch (error) {
            console.error("Error running watermark check:", error);
            if (!isAutoCheck) {
                showCustomDialog(
                    'error',
                    t('ios.checkFailedTitle'),
                    <>
                        {t('ios.checkFailedMessage')}{' '}
                        <a href="https://discord.gg/fprRUxfyu4" target="_blank" rel="noopener noreferrer" style={{ color: '#5865F2', textDecoration: 'underline' }}>
                            Discord
                        </a>
                    </>,
                    'ti-alert-circle'
                );
            }
        } finally {
            setIsCheckingWatermark(false);
            checkInProgressRef.current = false;
        }
    };

    // Reload monthly usage and badges when generation completes
    useEffect(() => {
        if (requestData?.status === 'openai_processing_complete' && currentUser) {
            // Add a small delay to ensure the backend trigger has time to update the count
            setTimeout(() => {
                loadMonthlyUsage(currentUser);
            }, 2000); // 2 second delay to allow backend processing
            
            // Fetch badges when generation completes
            // Badges are awarded at geodata_created stage, so they should be available now
            if (requestId && (isAdmin || badgesVisibleToAll)) {
                console.log('[SharePage] Fetching badges on generation complete');
                fetchAvailableBadges(requestId);
            }
        }
    }, [requestData?.status, currentUser, requestId, isAdmin, badgesVisibleToAll]);
    
    // Fetch badges when they are awarded (at geodata_created stage)
    useEffect(() => {
        if (requestData?.status === 'geodata_created' && currentUser && requestId && (isAdmin || badgesVisibleToAll)) {
            // Add a small delay to ensure badges are saved to the database
            setTimeout(() => {
                console.log('[SharePage] Fetching badges after geodata_created status');
                fetchAvailableBadges(requestId);
            }, 1000); // 1 second delay to allow backend to save badges
        }
    }, [requestData?.status, currentUser, requestId, isAdmin, badgesVisibleToAll]);

    // Check for activity credit status
    const checkActivityCreditStatus = async (activityId) => {
        if (!currentUser || !activityId) return;
        
        setIsCheckingActivityCredit(true);
        try {
            const checkCreditFunc = httpsCallable(functions, 'checkActivityWatermarkCredit');
            const result = await checkCreditFunc({ activityId });
            setActivityHasClaimedCredit(result.data.claimed);
        } catch (error) {
            console.error('Error checking activity credit status:', error);
            // On error, assume not claimed to allow user to try
            setActivityHasClaimedCredit(false);
        } finally {
            setIsCheckingActivityCredit(false);
        }
    };

    // Check for existing watermark when we have both request data and user
    useEffect(() => {
        if (requestId && requestData?.StravaId && currentUser) {
            // Show watermark button once map is complete
            if (requestData.status === 'openai_processing_complete') {
                setShowWatermarkButton(true);
                // Check if this activity has already claimed credit
                checkActivityCreditStatus(requestData.StravaId);
            }
        }
    }, [requestId, requestData?.StravaId, requestData?.status, currentUser]);

    // Auto-check for watermark when returning from Strava share
    useEffect(() => {
        // Use a ref to track if we've already auto-checked in this session
        const autoCheckedRef = { current: false };
        
        const checkPendingWatermarkShare = async () => {
            // Early exit if already auto-checked or check in progress
            if (autoCheckedRef.current || checkInProgressRef.current) {
                console.log('Auto-check already performed or in progress, skipping...');
                return;
            }
            
            if (!currentUser || !requestId || !requestData?.StravaId || activityHasClaimedCredit || hasExistingWatermark) {
                return;
            }
            
            try {
                const pendingDataStr = localStorage.getItem(WATERMARK_SHARE_PENDING_KEY);
                if (!pendingDataStr) return;
                
                const pendingData = JSON.parse(pendingDataStr);
                
                // Check if this is the same user, activity, request, AND session
                if (pendingData.userId === currentUser.uid && 
                    pendingData.activityId === requestData.StravaId && 
                    pendingData.requestId === requestId &&
                    pendingData.sessionId === sessionIdRef.current) {
                    
                    // Check if it's been less than 1 hour since they clicked share
                    const timeDiff = Date.now() - pendingData.timestamp;
                    if (timeDiff < 60 * 60 * 1000) { // 1 hour
                        // Mark that we're attempting auto-check
                        autoCheckedRef.current = true;
                        
                        // Set stravaShareInitiated to true to show the claim button
                        setStravaShareInitiated(true);
                        
                        // Run the check immediately
                        if (!checkInProgressRef.current && !activityHasClaimedCredit) {
                            posthog?.capture('watermark_auto_check_triggered', {
                                request_id: requestId,
                                activity_id: requestData.StravaId,
                                time_since_share: timeDiff
                            });
                            runWatermarkCheck(true, true); // isAutoCheck = true, isInitialAutoCheck = true
                        }
                    } else {
                        // Remove stale pending data
                        localStorage.removeItem(WATERMARK_SHARE_PENDING_KEY);
                    }
                } else if (pendingData.userId === currentUser.uid && 
                           pendingData.activityId === requestData.StravaId && 
                           pendingData.requestId === requestId) {
                    // Same user/activity/request but different session - clear the pending data
                    localStorage.removeItem(WATERMARK_SHARE_PENDING_KEY);
                }
            } catch (error) {
                console.error('Error checking pending watermark share:', error);
            }
        };
        
        // Check on mount
        checkPendingWatermarkShare();
        
        // Debounce timer ref
        let debounceTimer = null;
        
        // Check when tab gains focus or becomes visible (with debouncing)
        const handleFocusOrVisibility = () => {
            // Clear any pending debounced calls
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            
            // Debounce the check to prevent rapid successive calls
            debounceTimer = setTimeout(() => {
                checkPendingWatermarkShare();
            }, 100); // 100ms debounce - fast but prevents duplicates
        };
        
        const handleFocus = () => {
            handleFocusOrVisibility();
        };
        
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                handleFocusOrVisibility();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [currentUser, requestId, requestData?.StravaId, activityHasClaimedCredit, hasExistingWatermark]);

    // AI message cycling effect
    useEffect(() => {
        let interval;
        
        if (requestData?.status === 'openai_processing') {
            // Set initial random message
            const randomIndex = Math.floor(Math.random() * aiProcessingMessages.length);
            setCurrentAiMessage(t(aiProcessingMessages[randomIndex]));
            
            // Set up interval to change message every 2-4 seconds
            interval = setInterval(() => {
                const randomIndex = Math.floor(Math.random() * aiProcessingMessages.length);
                setCurrentAiMessage(t(aiProcessingMessages[randomIndex]));
            }, 2000 + Math.random() * 2000); // Random interval between 2-4 seconds
        } else {
            setCurrentAiMessage('');
        }
        
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [requestData?.status]);

        useEffect(() => {
        if (!requestId) {
            setError("No request ID provided in the URL.");
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        const docRef = doc(db, "GenerationRequests", requestId);
        
        // Set up real-time listener
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                console.log('SharePage: Firestore data received:', {
                    id: requestId,
                    status: data.status,
                    error: data.error,
                    errorType: typeof data.error,
                    errorTrimmed: data.error ? data.error.trim() : null,
                    hasOriginalImage: !!data.originalImagePath,
                    hasGeneratedImage: !!data.generatedImagePath
                });
                
                setRequestData(data);

                // Fetch badges when styleName becomes available and we haven't fetched yet
                // Note: currentUser might not be loaded yet, we'll handle that in a separate useEffect
                if (data.styleName &&
                    currentUser &&
                    !badgesEarlyFetchedRef.current &&
                    availableBadges.length === 0 &&
                    !isLoadingBadges) {
                    console.log('[SharePage] styleName detected, fetching badges:', data.styleName);
                    badgesEarlyFetchedRef.current = true;
                    fetchAvailableBadges(requestId);
                }

                // Load saved badge metadata if available
                if (data.badgeMetadata && data.badgeMetadata.badges && data.badgeMetadata.badges.length > 0) {
                    const savedBadges = data.badgeMetadata.badges;
                    
                    // Set selected badges
                    setSelectedBadges(savedBadges);
                    
                    // Set badge positions
                    const positions = {};
                    const sizes = {};
                    savedBadges.forEach(badge => {
                        if (badge.position) {
                            positions[badge.id] = badge.position;
                        }
                        if (badge.size !== undefined) {
                            sizes[badge.id] = badge.size;
                        }
                    });
                    setBadgePositions(positions);
                    setBadgeSizes(sizes);
                    
                    // Mark that we've loaded badges from saved data
                    setHasLoadedBadges(true);
                    
                    console.log('Loaded saved badge metadata:', {
                        badges: savedBadges.length,
                        positions,
                        sizes
                    });
                }
                
                // Fetch model-specific stats if model is available
                if (data.generationModel) {
                    fetchModelSpecificStats(data.generationModel);
                }
                
                // Update watermark status
                setWatermarkCheckStatus(data.watermarkCheckStatus || null);
                setWatermarkCheckError(data.watermarkCheckError || null);
                setHasExistingWatermark(data.hasWatermark || false);
                
                // Update feedback if it exists
                if (data.userFeedback) {
                    setUserFeedback(data.userFeedback);
                }
                
                // Update public visibility status
                if (typeof data.isPublic === 'boolean') {
                    setIsPublic(data.isPublic);
                }
                
                // Update regeneration info
                if (data.isRegeneration || data.regenerationNumber) {
                    const rootId = data.rootRequestId || requestId;
                    // Calculate remaining regenerations
                    const maxRegenerations = 3;
                    const currentNumber = data.regenerationNumber || 1;
                    const remaining = Math.max(0, maxRegenerations - currentNumber);
                    
                    setRegenerationInfo({
                        isRegeneration: true,
                        regenerationNumber: currentNumber,
                        remainingRegenerations: remaining
                    });
                }
                
                // Check for errors in the error field specifically
                const hasError = data.error && data.error.trim() !== '';
                console.log('SharePage: Error detection:', {
                    hasError,
                    errorField: data.error,
                    errorLength: data.error ? data.error.length : 0,
                    errorTrimmedLength: data.error ? data.error.trim().length : 0
                });
                
                if (hasError) {
                    // Stop progress bar and show the exact error content
                    console.log('SharePage: Setting error state:', data.error);
                    setProgressPercentage(100);
                    setEstimatedTimeRemaining(null);
                    setError(data.error);
                    setImageDisplayVisible(false);
                } else if (data.status === 'openai_processing_complete') {
                    // Generation completed successfully
                    setProgressPercentage(100);
                    setEstimatedTimeRemaining(null);
                } else if (data.generationStartTime && data.status !== 'openai_processing_complete') {
                    // Update progress tracking for in-progress generation
                    const currentTime = new Date();
                    const progress = calculateProgress(data.status, data.generationStartTime, currentTime);
                    const eta = calculateETA(progress, data.generationStartTime, currentTime);
                    
                    setProgressPercentage(progress);
                    setEstimatedTimeRemaining(eta);
                    setGenerationStartTime(data.generationStartTime);
                }
                
                // Check if generation is complete (and no errors)
                if (!hasError && data.originalImagePath && data.generatedImagePath) {
                    try {
                        const origUrl = await getDownloadURL(ref(storage, data.originalImagePath));
                        const genUrl = await getDownloadURL(ref(storage, data.generatedImagePath));

                        let imagesLoaded = 0;
                        const totalImages = 2;
                        const onImageLoad = () => {
                            imagesLoaded++;
                            if (imagesLoaded === totalImages) {
                                setOriginalImageSrc(origUrl);
                                setGeneratedImageSrc(genUrl);
                                setImageDisplayVisible(true);
                                setError(null); // Clear any previous errors
                                
                                // Track successful image loading
                                posthog?.capture('share_page_images_loaded', {
                                    request_id: requestId,
                                    strava_activity_id: requestData?.StravaId,
                                    status: requestData?.status,
                                    user_authenticated: !!currentUser,
                                    load_time_ms: Date.now() - (window.sharePageLoadStart || Date.now())
                                });
                                
                                // Set initial clip path to 50% after images are loaded
                                setTimeout(() => {
                                    updateClipPath(50);
                                }, 0);
                                
                                // Fetch available badges for this activity (admin only)
                                console.log('[SharePage] Image load - checking badges:', {
                                    hasCurrentUser: !!currentUser,
                                    currentUserId: currentUser?.uid,
                                    requestId: requestId,
                                    isAdmin: isAdmin
                                });
                                
                                if (currentUser && requestId && (isAdmin || badgesVisibleToAll)) {
                                    console.log('[SharePage] Fetching badges for request:', requestId, {
                                        isAdmin,
                                        badgesVisibleToAll,
                                        userId: currentUser.uid
                                    });
                                    fetchAvailableBadges(requestId);
                                } else {
                                    console.log('[SharePage] Not fetching badges on image load:', {
                                        reason: !currentUser ? 'No currentUser' : !requestId ? 'No requestId' : 'Badges not visible to non-admins',
                                        isAdmin,
                                        badgesVisibleToAll
                                    });
                                }
                            }
                        };
                        const img1 = new Image(); 
                        img1.onload = onImageLoad; 
                        img1.onerror = () => { 
                            setError('Failed to load original image.'); 
                            onImageLoad(); 
                        }; 
                        img1.src = origUrl;
                        
                        const img2 = new Image(); 
                        img2.onload = onImageLoad; 
                        img2.onerror = () => { 
                            setError('Failed to load generated image.'); 
                            onImageLoad(); 
                        }; 
                        img2.src = genUrl;

                    } catch (e) {
                        console.error("Error getting image URLs for share page:", e);
                        setError("Could not load images for this request.");
                        setImageDisplayVisible(false);
                    }
                } else if (!hasError) {
                    // Generation is still in progress and no error - clear any existing error and show status
                    console.log('SharePage: Clearing error state - no error detected');
                    setError(null);
                    setImageDisplayVisible(false);
                }
                
                setIsLoading(false);
            } else {
                setError("Request not found. It might have been deleted or the ID is incorrect.");
                setIsLoading(false);
            }
        }, (err) => {
            console.error("Error listening to request for share page:", err);
            setError("Failed to load request details.");
            setIsLoading(false);
        });
        
        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [requestId]);

    // Update progress in real-time while generation is in progress
    useEffect(() => {
        let interval;
        
        // Only update progress if there's no error in the error field
        const hasError = requestData?.error && requestData.error.trim() !== '';
        
        if (generationStartTime && requestData?.status && requestData.status !== 'openai_processing_complete' && !hasError) {
            interval = setInterval(() => {
                const currentTime = new Date();
                const progress = calculateProgress(requestData.status, generationStartTime, currentTime);
                const eta = calculateETA(progress, generationStartTime, currentTime);
                
                setProgressPercentage(progress);
                setEstimatedTimeRemaining(eta);
            }, 1000); // Update every second
        }
        
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [generationStartTime, requestData?.status, requestData?.error, averageGenerationTime]);

    // Also add a useEffect to update clip path when imageDisplayVisible changes
    useEffect(() => {
        if (imageDisplayVisible) {
            updateClipPath(sliderValue);
        }
    }, [imageDisplayVisible, sliderValue]);

    // Hide badge instructions after 30s on final page and show toggle title
    useEffect(() => {
        if (imageDisplayVisible) {
            const timer = setTimeout(() => setShowBadgeInstructions(false), 30000);
            return () => clearTimeout(timer);
        }
    }, [imageDisplayVisible]);

    useEffect(() => {
        if (!isAdmin) return;
        if (!requestId) return;
        if (!imageDisplayVisible) return;
        if (!requestData?.generatedImagePath) return;
        if (contourDetailsFetchedRef.current) return;

        contourDetailsFetchedRef.current = true;

        const fetchContourDetails = async () => {
            try {
                const getGenerationDetails = httpsCallable(functions, 'getGenerationDetailsForAdmin');
                const result = await getGenerationDetails({ generationId: requestId });
                setContourImageUrl(result.data?.contourImageUrl || null);
                setContourPrompt(result.data?.contourPrompt || '');
            } catch (err) {
                console.error('[SharePage] Failed to fetch contour details:', err);
                contourDetailsFetchedRef.current = false;
                setIsContourOverlayOpen(false);
            }
        };

        fetchContourDetails();
    }, [isAdmin, requestId, requestData?.generatedImagePath, imageDisplayVisible, functions]);

    // Handle slider when badges are added/removed
    useEffect(() => {
        if (selectedBadges.length > 0) {
            // Lower slider completely to show only generated image
            setSliderValue(100);
            updateClipPath(100);
        } else {
            // Reset slider to middle position when no badges
            setSliderValue(50);
            updateClipPath(50);
        }
    }, [selectedBadges.length]);


    // Upload composite image to Firebase Storage
    const uploadCompositeImage = async (blob) => {
        try {
            const timestamp = Date.now();
            // Use similar path structure as openai_images
            const filename = `composite-maps/${currentUser?.uid || 'anonymous'}/${requestId}/${timestamp}.png`;
            const storageRef = ref(storage, filename);
            
            console.log('Uploading to path:', filename);
            
            // Upload the blob with metadata
            const metadata = {
                contentType: 'image/png',
                customMetadata: {
                    requestId: requestId,
                    timestamp: timestamp.toString()
                }
            };
            
            const uploadResult = await uploadBytes(storageRef, blob, metadata);
            console.log('Upload completed:', uploadResult);
            
            // Get the download URL
            const downloadUrl = await getDownloadURL(storageRef);
            console.log('Got download URL:', downloadUrl);
            
            return downloadUrl;
        } catch (error) {
            console.error('Error uploading composite image:', error);
            throw error;
        }
    };

    const createMapWithBadges = async () => {
        return new Promise(async (resolve, reject) => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                
                // Always use crossOrigin for all images
                img.crossOrigin = 'anonymous';
                
                img.onload = async () => {
                // Set canvas size to match image
                canvas.width = img.width;
                canvas.height = img.height;
                
                // Draw the main image
                ctx.drawImage(img, 0, 0);
                
                // Draw badges if any
                if (selectedBadges.length > 0) {
                    // Load all badge images first
                    const badgeImagePromises = selectedBadges.map(badge => 
                        new Promise((res) => {
                            const badgeImg = new Image();
                            badgeImg.crossOrigin = 'anonymous';
                            badgeImg.onload = () => res({ badge, img: badgeImg, loaded: true });
                            badgeImg.onerror = () => {
                                console.error('Failed to load badge image:', badge.imageUrl);
                                res({ badge, img: null, loaded: false });
                            };
                            badgeImg.src = badge.imageUrl;
                        })
                    );
                    
                    const badgeImages = await Promise.all(badgeImagePromises);
                    
                    // Draw each badge
                    badgeImages.forEach(({ badge, img: badgeImg, loaded }) => {
                        if (!loaded || !badgeImg) {
                            console.warn('Skipping badge due to load failure:', badge.name);
                            return;
                        }
                        
                        const position = badgePositions[badge.id] || { top: 3, left: 3 };
                        
                        // Calculate badge dimensions and position
                        const badgeSize = badgeSizes[badge.id] || badge.size || 10;
                        const badgeWidth = (badgeSize / 100) * img.width;
                        const badgeHeight = badgeWidth; // Square aspect ratio
                        const x = (position.left / 100) * img.width;
                        const y = (position.top / 100) * img.height;
                        
                        // Draw the badge image directly without background
                        ctx.drawImage(
                            badgeImg, 
                            x, 
                            y, 
                            badgeWidth, 
                            badgeHeight
                        );
                    });
                }
                
                // Try to export the canvas
                try {
                    canvas.toBlob(blob => {
                        if (blob) {
                            console.log('Created blob successfully, size:', blob.size);
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to create blob from canvas'));
                        }
                    }, 'image/png', 0.95); // Use 95% quality to reduce file size
                } catch (error) {
                    console.error('Canvas export error:', error);
                    reject(new Error('Canvas is tainted and cannot be exported. This may be due to CORS restrictions.'));
                }
            };
            
            img.onerror = () => {
                reject(new Error('Failed to load main image'));
            };
            
            img.src = generatedImageSrc;
            } catch (error) {
                console.error('Error in createMapWithBadges:', error);
                reject(error);
            }
        });
    };

    const handleDownloadAiImage = async () => {
        posthog?.capture('map_downloaded', {
            request_id: requestId,
            download_type: 'ai_image',
            is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
            download_source: 'download_button',
            strava_activity_id: requestData?.StravaId,
            user_authenticated: !!currentUser,
            has_badges: selectedBadges.length > 0
        });
        
        if (!generatedImageSrc || !requestId) {
            alert("Generated image URL is not available yet.");
            return;
        }

        // Check if device is iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
            // Get download button reference outside try block
            const downloadButton = document.querySelector('.download-ai-button');
            const originalButtonText = downloadButton ? downloadButton.textContent : '';
            
            try {
                // Show loading state for download button
                if (downloadButton) {
                    downloadButton.textContent = 'Preparing download...';
                    downloadButton.disabled = true;
                }

                let imageUrlToUse = generatedImageSrc;

                // If badges are present, create composite image
                if (selectedBadges.length > 0) {
                    try {
                        // Show upload progress immediately
                        setIsUploadingComposite(true);
                        
                        const blob = await createMapWithBadges();
                        
                        // Upload composite image to Firebase Storage
                        console.log('Uploading composite image to Firebase Storage...');
                        const compositeUrl = await uploadCompositeImage(blob);
                        console.log('Composite image uploaded:', compositeUrl);
                        
                        // Update Firestore with composite URL and mark as having badges
                        if (requestId && currentUser) {
                            try {
                                const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                                
                                // Prepare badge metadata
                                const badgeMetadata = {
                                    badges: selectedBadges.map(badge => ({
                                        id: badge.id,
                                        name: badge.name,
                                        imageUrl: badge.imageUrl,
                                        position: badgePositions[badge.id] || { top: 3, left: 3 },
                                        size: badgeSizes[badge.id] || badge.size || 10
                                    }))
                                };
                                
                                await updateCompositeImageUrl({ 
                                    requestId, 
                                    compositeImageUrl: compositeUrl,
                                    badgeMetadata
                                });
                                console.log('Firestore updated with composite URL, hasBadges flag, and badge metadata');
                            } catch (err) {
                                console.error('Failed to update composite URL in Firestore:', err);
                                // Continue anyway - the image upload succeeded
                            }
                        }
                        
                        // Use the Firebase Storage URL
                        imageUrlToUse = compositeUrl;
                    } catch (compositeError) {
                        console.error('Failed to create composite image with badges:', compositeError);
                        alert('Unable to include badges in the download. The image will be downloaded without badges.');
                        // Fall back to original image without badges
                        imageUrlToUse = generatedImageSrc;
                    } finally {
                        setIsUploadingComposite(false);
                    }
                } else {
                    // No badges - clear any existing badge metadata
                    if (requestId && currentUser) {
                        try {
                            const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                            
                            // Clear badge metadata by passing empty badges array
                            await updateCompositeImageUrl({ 
                                requestId, 
                                compositeImageUrl: null,
                                badgeMetadata: { badges: [] }
                            });
                            console.log('Badge metadata cleared from Firestore');
                        } catch (err) {
                            console.error('Failed to clear badge metadata in Firestore:', err);
                            // Continue anyway
                        }
                    }
                }

                // For iOS devices: Redirect to special iOS download page
                let iosUrl = `/ios-download/${requestId}?imageUrl=${encodeURIComponent(imageUrlToUse)}`;

                // Only add Strava ID if it exists
                if (requestData && requestData.StravaId) {
                    iosUrl += `&stravaId=${requestData.StravaId}`;
                }

                posthog?.capture('ios_download_redirect', {
                    request_id: requestId,
                    download_source: 'download_button',
                    strava_activity_id: requestData?.StravaId,
                    has_badges: selectedBadges.length > 0
                });

                window.location.href = iosUrl;
            } catch (error) {
                console.error('Error preparing iOS download:', error);
                alert('Failed to prepare download. Please try again.');
                if (downloadButton) {
                    downloadButton.textContent = originalButtonText;
                    downloadButton.disabled = false;
                }
            }
            return;
        }

        // For non-iOS devices: Continue with the normal download process
        setIsDownloading(true);
        try {
            let blob;
            
            // If badges are present, create composite image
            if (selectedBadges.length > 0) {
                blob = await createMapWithBadges();
                
                // Upload composite image to Firebase Storage in background
                // Don't await this - let it happen in background
                uploadCompositeImage(blob).then(async compositeUrl => {
                    console.log('Background upload completed:', compositeUrl);
                    // Update Firestore with composite URL using Cloud Function
                    if (requestId && currentUser) {
                        try {
                            const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                            
                            // Prepare badge metadata
                            const badgeMetadata = {
                                badges: selectedBadges.map(badge => ({
                                    id: badge.id,
                                    name: badge.name,
                                    imageUrl: badge.imageUrl,
                                    position: badgePositions[badge.id] || { top: 3, left: 3 },
                                    size: badgeSizes[badge.id] || badge.size || 10
                                }))
                            };
                            
                            await updateCompositeImageUrl({ 
                                requestId, 
                                compositeImageUrl: compositeUrl,
                                badgeMetadata
                            });
                            console.log('Firestore updated with composite URL and badge metadata');
                        } catch (err) {
                            console.error('Failed to update composite URL in Firestore:', err);
                            // Fail silently - the upload succeeded even if we can't update Firestore
                        }
                    }
                }).catch(err => console.error('Background upload failed:', err));
            } else {
                // No badges, download original image
                const response = await fetch(generatedImageSrc);
                if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
                blob = await response.blob();
                
                // Clear any existing badge metadata
                if (requestId && currentUser) {
                    try {
                        const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                        
                        // Clear badge metadata by passing empty badges array
                        await updateCompositeImageUrl({ 
                            requestId, 
                            compositeImageUrl: "",
                            badgeMetadata: { badges: [] }
                        });
                        console.log('Badge metadata cleared from Firestore');
                    } catch (err) {
                        console.error('Failed to clear badge metadata in Firestore:', err);
                        // Continue anyway
                    }
                }
            }
            
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `mythic_map_ai_${requestId}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
            
            posthog?.capture('map_download_completed', {
                request_id: requestId,
                download_method: 'browser',
                download_source: 'download_button',
                strava_activity_id: requestData?.StravaId,
                has_badges: selectedBadges.length > 0
            });
        } catch (error) {
            console.error("Error downloading AI image on share page:", error);
            alert(`Failed to download image: ${error.message}`);
            
            posthog?.capture('map_download_failed', {
                request_id: requestId,
                error: error.message,
                download_source: 'download_button',
                strava_activity_id: requestData?.StravaId,
                has_badges: selectedBadges.length > 0
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const handleStravaDownload = async () => {

        // Set the state to indicate Strava share has been initiated
        setStravaShareInitiated(true);
        
        // Save pending state to localStorage for auto-check on return
        if (currentUser && requestData?.StravaId) {
            const pendingData = {
                userId: currentUser.uid,
                activityId: requestData.StravaId,
                requestId: requestId,
                timestamp: Date.now(),
                sessionId: sessionIdRef.current // Add session ID to track current page session
            };
            localStorage.setItem(WATERMARK_SHARE_PENDING_KEY, JSON.stringify(pendingData));
        }

        posthog?.capture('map_downloaded_strava', {
            request_id: requestId,
            download_type: 'ai_image',
            is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
            download_source: 'strava_button',
            strava_activity_id: requestData?.StravaId,
            user_authenticated: !!currentUser,
            has_badges: selectedBadges.length > 0
        });

        if (!generatedImageSrc || !requestId) {
            setShareInstructions("Generated image URL is not available yet.");
            setTimeout(() => setShareInstructions(''), 3000);
            return;
        }

        // Check if device is iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
            try {
                // Show loading state
                setShareInstructions("Preparing download...");

                let imageUrlToUse = generatedImageSrc;

                // If badges are present, create composite image
                if (selectedBadges.length > 0) {
                    try {
                        // Show upload progress immediately
                        setIsUploadingComposite(true);
                        
                        const blob = await createMapWithBadges();
                        
                        // Upload composite image to Firebase Storage
                        console.log('Uploading composite image to Firebase Storage...');
                        const compositeUrl = await uploadCompositeImage(blob);
                        console.log('Composite image uploaded:', compositeUrl);
                        
                        // Update Firestore with composite URL and mark as having badges
                        if (requestId && currentUser) {
                            try {
                                const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                                
                                // Prepare badge metadata
                                const badgeMetadata = {
                                    badges: selectedBadges.map(badge => ({
                                        id: badge.id,
                                        name: badge.name,
                                        imageUrl: badge.imageUrl,
                                        position: badgePositions[badge.id] || { top: 3, left: 3 },
                                        size: badgeSizes[badge.id] || badge.size || 10
                                    }))
                                };
                                
                                await updateCompositeImageUrl({ 
                                    requestId, 
                                    compositeImageUrl: compositeUrl,
                                    badgeMetadata
                                });
                                console.log('Firestore updated with composite URL, hasBadges flag, and badge metadata');
                            } catch (err) {
                                console.error('Failed to update composite URL in Firestore:', err);
                                // Continue anyway - the image upload succeeded
                            }
                        }
                        
                        // Use the Firebase Storage URL
                        imageUrlToUse = compositeUrl;
                    } catch (compositeError) {
                        console.error('Failed to create composite image with badges:', compositeError);
                        alert('Unable to include badges in the download. The image will be downloaded without badges.');
                        // Fall back to original image without badges
                        imageUrlToUse = generatedImageSrc;
                    } finally {
                        setIsUploadingComposite(false);
                    }
                } else {
                    // No badges - clear any existing badge metadata
                    if (requestId && currentUser) {
                        try {
                            const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                            
                            // Clear badge metadata by passing empty badges array
                            await updateCompositeImageUrl({ 
                                requestId, 
                                compositeImageUrl: null,
                                badgeMetadata: { badges: [] }
                            });
                            console.log('Badge metadata cleared from Firestore');
                        } catch (err) {
                            console.error('Failed to clear badge metadata in Firestore:', err);
                            // Continue anyway
                        }
                    }
                }

                // For iOS devices: Redirect to special iOS download page
                let iosUrl = `/ios-download/${requestId}?imageUrl=${encodeURIComponent(imageUrlToUse)}`;

                // Only add Strava ID if it exists
                if (requestData && requestData.StravaId) {
                    iosUrl += `&stravaId=${requestData.StravaId}`;
                }

                posthog?.capture('ios_download_redirect', {
                    request_id: requestId,
                    download_source: 'strava_button',
                    strava_activity_id: requestData?.StravaId,
                    has_badges: selectedBadges.length > 0
                });

                window.location.href = iosUrl;
            } catch (error) {
                console.error('Error preparing iOS download:', error);
                setShareInstructions("Failed to prepare download. Please try again.");
                setTimeout(() => setShareInstructions(''), 3000);
            }
            return;
        }

        // For non-iOS devices: Continue with the normal download process
        setIsDownloading(true);
        try {
            let blob;
            
            // If badges are present, create composite image
            if (selectedBadges.length > 0) {
                blob = await createMapWithBadges();
                
                // Upload composite image to Firebase Storage in background
                // Don't await this - let it happen in background
                uploadCompositeImage(blob).then(async compositeUrl => {
                    console.log('Background upload completed:', compositeUrl);
                    // Update Firestore with composite URL using Cloud Function
                    if (requestId && currentUser) {
                        try {
                            const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                            
                            // Prepare badge metadata
                            const badgeMetadata = {
                                badges: selectedBadges.map(badge => ({
                                    id: badge.id,
                                    name: badge.name,
                                    imageUrl: badge.imageUrl,
                                    position: badgePositions[badge.id] || { top: 3, left: 3 },
                                    size: badgeSizes[badge.id] || badge.size || 10
                                }))
                            };
                            
                            await updateCompositeImageUrl({ 
                                requestId, 
                                compositeImageUrl: compositeUrl,
                                badgeMetadata
                            });
                            console.log('Firestore updated with composite URL and badge metadata');
                        } catch (err) {
                            console.error('Failed to update composite URL in Firestore:', err);
                            // Fail silently - the upload succeeded even if we can't update Firestore
                        }
                    }
                }).catch(err => console.error('Background upload failed:', err));
            } else {
                // No badges, download original image
                const response = await fetch(generatedImageSrc);
                if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
                blob = await response.blob();
                
                // Clear any existing badge metadata
                if (requestId && currentUser) {
                    try {
                        const updateCompositeImageUrl = httpsCallable(functions, 'updateCompositeImageUrl');
                        
                        // Clear badge metadata by passing empty badges array
                        await updateCompositeImageUrl({ 
                            requestId, 
                            compositeImageUrl: "",
                            badgeMetadata: { badges: [] }
                        });
                        console.log('Badge metadata cleared from Firestore');
                    } catch (err) {
                        console.error('Failed to clear badge metadata in Firestore:', err);
                        // Continue anyway
                    }
                }
            }
            
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `mythic_map_ai_${requestId}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);

            posthog?.capture('map_download_completed', {
                request_id: requestId,
                download_method: 'browser',
                download_source: 'strava_button',
                strava_activity_id: requestData?.StravaId,
                has_badges: selectedBadges.length > 0
            });

            // Strava-specific instructions
            if (requestData && requestData.StravaId) {
                setShareInstructions(
                    <div>
                        <strong>{t('share.strava.title', 'Image successfully downloaded! To add to Strava:')}</strong>
                        <ol>
                            <li>{t('share.strava.step1', 'Click "Edit" on your activity')}</li>
                            <li>{t('share.strava.step2', 'Add photos')}</li>
                            <li>{t('share.strava.step3', 'Upload {{file}} from your Downloads folder', { file: `mythic_map_ai_${requestId}.png` })}</li>
                        </ol>
                    </div>
                );
                
                posthog?.capture('strava_edit_link_opened', {
                    request_id: requestId,
                    strava_activity_id: requestData.StravaId,
                    delay_seconds: 1.5
                });
                
                // Open Strava edit page after a short delay
                setTimeout(() => {
                    const stravaEditUrl = `https://www.strava.com/activities/${requestData.StravaId}/edit`;
                    window.open(stravaEditUrl, '_blank');
                }, 1500);
            } else {
                setShareInstructions(t('share.downloaded', 'Image downloaded successfully!'));
            }
        } catch (error) {
            console.error("Error downloading AI image on share page:", error);
            setShareInstructions(t('share.downloadFailed', 'Failed to download image: {{msg}}', { msg: error.message }));
            
            posthog?.capture('map_download_failed', {
                request_id: requestId,
                error: error.message,
                download_source: 'strava_button',
                strava_activity_id: requestData?.StravaId,
                has_badges: selectedBadges.length > 0
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSliderChange = (event) => {
        // Don't allow slider change if badges are present
        if (selectedBadges.length > 0) return;
        
        const newSliderValue = parseFloat(event.target.value);
        setSliderValue(newSliderValue);
        updateClipPath(newSliderValue);
        
        // Track slider interaction - throttled to avoid too many events
        if (!window.sliderTrackingTimeout) {
            posthog?.capture('image_comparison_slider_used', {
                request_id: requestId,
                slider_position: Math.round(newSliderValue),
                strava_activity_id: requestData?.StravaId,
                page: 'share'
            });
            
            // Throttle tracking to once every 2 seconds
            window.sliderTrackingTimeout = setTimeout(() => {
                window.sliderTrackingTimeout = null;
            }, 2000);
        }
    };

    const startDrag = (e) => {
        // Don't allow slider drag if badges are present
        if (selectedBadges.length > 0) return;
        
        // Don't start slider drag if clicking on a badge
        const target = e.target;
        const isClickOnBadge = target.closest('.map-badge-overlay');
        if (isClickOnBadge || draggingBadge) return;
        
        isDraggingRef.current = true;
        if (e.type === 'touchstart' && e.cancelable) e.preventDefault();
        
        // Track start of drag interaction
        posthog?.capture('image_comparison_drag_started', {
            request_id: requestId,
            interaction_type: e.type.includes('touch') ? 'touch' : 'mouse',
            strava_activity_id: requestData?.StravaId,
            page: 'share'
        });
    };
    const onDrag = (e) => {
        // Don't move slider if dragging a badge
        if (draggingBadge || !isDraggingRef.current || !imageComparisonDivRef.current) return;
        const event = e.touches ? e.touches[0] : e;
        const rect = imageComparisonDivRef.current.getBoundingClientRect();
        let y = event.clientY - rect.top;
        let percentage = (y / rect.height) * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        setSliderValue(percentage); // This will trigger re-render and updateClipPath via handleSliderChange or directly
        updateClipPath(percentage); // Explicitly call to ensure clip path updates during drag
        if (e.type === 'touchmove' && e.cancelable) e.preventDefault();
    };
    const stopDrag = () => { 
        if (isDraggingRef.current) {
            posthog?.capture('image_comparison_drag_ended', {
                request_id: requestId,
                final_position: Math.round(sliderValue),
                strava_activity_id: requestData?.StravaId,
                page: 'share'
            });
        }
        isDraggingRef.current = false; 
    };

    useEffect(() => {
        const div = imageComparisonDivRef.current;
        if (div && imageDisplayVisible) {
            document.addEventListener('mousemove', onDrag);
            document.addEventListener('touchmove', onDrag, { passive: false });
            document.addEventListener('mouseup', stopDrag);
            document.addEventListener('touchend', stopDrag);
            return () => {
                document.removeEventListener('mousemove', onDrag);
                document.removeEventListener('touchmove', onDrag);
                document.removeEventListener('mouseup', stopDrag);
                document.removeEventListener('touchend', stopDrag);
            };
        }
    }, [imageDisplayVisible]);

    // Add event listeners for badge dragging
    useEffect(() => {
        if (draggingBadge) {
            document.addEventListener('mousemove', handleBadgeDrag);
            document.addEventListener('touchmove', handleBadgeDrag, { passive: false });
            document.addEventListener('mouseup', handleBadgeDragEnd);
            document.addEventListener('touchend', handleBadgeDragEnd);
            
            return () => {
                document.removeEventListener('mousemove', handleBadgeDrag);
                document.removeEventListener('touchmove', handleBadgeDrag);
                document.removeEventListener('mouseup', handleBadgeDragEnd);
                document.removeEventListener('touchend', handleBadgeDragEnd);
            };
        }
    }, [draggingBadge]);
    
    // Add keyboard controls for badge resizing
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!selectedBadgeId) return;
            
            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                handleBadgeResize(selectedBadgeId, 1);
            } else if (e.key === '-' || e.key === '_') {
                e.preventDefault();
                handleBadgeResize(selectedBadgeId, -1);
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                // Remove the badge
                setSelectedBadges(prev => prev.filter(b => b.id !== selectedBadgeId));
                setBadgePositions(prev => {
                    const newPositions = { ...prev };
                    delete newPositions[selectedBadgeId];
                    return newPositions;
                });
                setBadgeSizes(prev => {
                    const newSizes = { ...prev };
                    delete newSizes[selectedBadgeId];
                    return newSizes;
                });
                setSelectedBadgeId(null);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setSelectedBadgeId(null);
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedBadgeId]);
    
    // Update slider position when badge is moved or resized
    useEffect(() => {
        const updateSliderPosition = () => {
            if (selectedBadgeId && imageComparisonDivRef.current) {
                const containerRect = imageComparisonDivRef.current.getBoundingClientRect();
                const badge = selectedBadges.find(b => b.id === selectedBadgeId);
                const position = badgePositions[selectedBadgeId];
                const badgeSize = badgeSizes[selectedBadgeId] || badge?.size || 10;
                
                if (position) {
                    // Calculate left edge of badge in pixels
                    const leftX = containerRect.left + (position.left * containerRect.width / 100);
                    // Position slider on top of badge - subtract 20px for spacing
                    const topY = containerRect.top + (position.top * containerRect.height / 100) - 20;
                    
                    setSliderPosition({
                        top: topY,
                        left: leftX
                    });
                }
            }
        };

        updateSliderPosition();

        // Add scroll listener to update position when page scrolls
        window.addEventListener('scroll', updateSliderPosition);
        window.addEventListener('resize', updateSliderPosition);

        return () => {
            window.removeEventListener('scroll', updateSliderPosition);
            window.removeEventListener('resize', updateSliderPosition);
        };
    }, [selectedBadgeId, badgePositions, badgeSizes, selectedBadges]);

    const handleCopyLink = () => {
        const shareUrl = window.location.href;
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
        
        posthog?.capture('map_link_copied', {
            request_id: requestId,
            strava_activity_id: requestData?.StravaId,
            share_method: 'clipboard',
            page: 'share'
        });
    };

    const handleShare = async (platform) => {
        posthog?.capture('map_shared', {
            request_id: requestId,
            platform: platform,
            share_method: 'social',
            strava_activity_id: requestData?.StravaId,
            has_strava_activity: !!requestData?.StravaId,
            user_authenticated: !!currentUser,
            has_badges: selectedBadges.length > 0
        });
        
        const shareUrl = window.location.href;
        const shareText = `Check out my epic adventure on Mythic Maps!`;

        // For Instagram and TikTok, download the image first
        if (platform === 'instagram' || platform === 'tiktok') {
            if (!generatedImageSrc || !requestId) {
                setShareInstructions(t('share.imageNotReady', 'Generated image URL is not available yet.'));
                setTimeout(() => setShareInstructions(''), 3000);
                return;
            }

            try {
                let blob;
                
                // If badges are present, create composite image
                if (selectedBadges.length > 0) {
                    blob = await createMapWithBadges();
                } else {
                    // No badges, download original image
                    const response = await fetch(generatedImageSrc);
                    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
                    blob = await response.blob();
                }
                
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = `mythic_map_ai_${requestId}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objectUrl);

                posthog?.capture('map_download_for_social', {
                    request_id: requestId,
                    platform: platform,
                    download_method: 'browser',
                    strava_activity_id: requestData?.StravaId,
                    has_badges: selectedBadges.length > 0
                });

                // Show platform-specific instructions
                if (platform === 'instagram') {
                    setShareInstructions(
                        <div>
                            <strong>{t('share.instagram.title', 'Image successfully downloaded! To share on Instagram:')}</strong>
                            <ol>
                                <li>{t('share.instagram.step1', 'Open Instagram')}</li>
                                <li>{t('share.instagram.step2', 'Create a new post')}</li>
                                <li>{t('share.instagram.step3', 'Select the downloaded image from your gallery')}</li>
                            </ol>
                        </div>
                    );
                } else if (platform === 'tiktok') {
                    setShareInstructions(
                        <div>
                            <strong>{t('share.tiktok.title', 'Image successfully downloaded! To share on TikTok:')}</strong>
                            <ol>
                                <li>{t('share.tiktok.step1', 'Open TikTok')}</li>
                                <li>{t('share.tiktok.step2', 'Create a new post')}</li>
                                <li>{t('share.tiktok.step3', 'Select the downloaded image from your gallery')}</li>
                            </ol>
                        </div>
                    );
                }
            } catch (error) {
                console.error("Error downloading image:", error);
                setShareInstructions(t('share.downloadFailed', 'Failed to download image: {{msg}}', { msg: error.message }));
                
                posthog?.capture('map_download_for_social_failed', {
                    request_id: requestId,
                    platform: platform,
                    error: error.message,
                    strava_activity_id: requestData?.StravaId,
                    has_badges: selectedBadges.length > 0
                });
            }
            return;
        }

        // For other platforms, proceed with web sharing
        switch (platform) {
            case 'twitter':
                window.open(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
                    '_blank'
                );
                break;

            case 'facebook':
                window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                    '_blank'
                );
                break;

            case 'reddit':
                window.open(
                    `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`,
                    '_blank'
                );
                break;
        }

        // Track social platform sharing
        posthog?.capture(`map_shared_${platform}`, {
            request_id: requestId,
            share_url: shareUrl,
            strava_activity_id: requestData?.StravaId,
            user_authenticated: !!currentUser,
            has_badges: selectedBadges.length > 0
        });
    };

    const handleStravaConnect = () => {
        posthog?.capture('strava_connect_clicked', {
            source: 'share_page',
            request_id: requestId,
            user_authenticated: !!currentUser,
            context: 'join_mythic_maps'
        });
        
        // Strava OAuth Configuration
        const STRAVA_CLIENT_ID = "159733"; // Your Strava Client ID
        const STRAVA_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
        const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write";

        // Include a state parameter for CSRF protection
        const state = Math.random().toString(36).substring(2);
        localStorage.setItem('stravaOAuthState', state);

        const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
        window.location.href = consentUrl;
    };
    
    const handleFeedback = async (feedbackType) => {
        if (isSubmittingFeedback || userFeedback === feedbackType) return;
        
        setIsSubmittingFeedback(true);
        const previousFeedback = userFeedback;
        setUserFeedback(feedbackType);
        
        try {
            const submitFeedbackFunc = httpsCallable(functions, 'submitGenerationFeedback');
            await submitFeedbackFunc({ 
                generationId: requestId,
                feedback: feedbackType 
            });
            
            posthog?.capture(feedbackType === 'like' ? 'map_liked' : 'map_disliked', {
                request_id: requestId,
                strava_activity_id: requestData?.StravaId,
                style: requestData?.style || requestData?.selectedTemplate,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error submitting feedback:', error);
            // Revert on error
            setUserFeedback(previousFeedback);
        } finally {
            setIsSubmittingFeedback(false);
        }
    };
    
    const handleToggleVisibility = async () => {
        if (isTogglingVisibility) return;
        
        setIsTogglingVisibility(true);
        const newVisibility = !isPublic;
        
        try {
            const toggleMapVisibilityFunc = httpsCallable(functions, 'toggleMapVisibility');
            const result = await toggleMapVisibilityFunc({ 
                requestId: requestId,
                isPublic: newVisibility 
            });
            
            if (result.data.success) {
                setIsPublic(newVisibility);
                posthog?.capture('map_visibility_toggled', {
                    request_id: requestId,
                    is_public: newVisibility,
                    strava_activity_id: requestData?.StravaId
                });
            }
        } catch (error) {
            console.error('Error toggling map visibility:', error);
            showCustomDialog(
                'error',
                'Failed to Update',
                'Could not update map visibility. Please try again.',
                'ti-alert-circle'
            );
        } finally {
            setIsTogglingVisibility(false);
        }
    };
    
    const handleBadgeSelect = (badge) => {
        setSelectedBadges(prevBadges => {
            const existingIndex = prevBadges.findIndex(b => b.id === badge.id);
            if (existingIndex !== -1) {
                // Remove badge if already selected
                setBadgePositions(prev => {
                    const newPositions = { ...prev };
                    delete newPositions[badge.id];
                    return newPositions;
                });
                setBadgeSizes(prev => {
                    const newSizes = { ...prev };
                    delete newSizes[badge.id];
                    return newSizes;
                });
                // Clear selection if this badge was selected
                if (selectedBadgeId === badge.id) {
                    setSelectedBadgeId(null);
                }
                return prevBadges.filter(b => b.id !== badge.id);
            } else {
                // Add badge to grid with initial position (in percentages)
                const index = prevBadges.length;
                const row = Math.floor(index / 3);
                const col = index % 3;
                const initialTop = 3 + (row * 8); // 3% margin, 8% spacing
                const initialLeft = 3 + (col * 8); // 3% margin, 8% spacing
                
                setBadgePositions(prev => ({
                    ...prev,
                    [badge.id]: { top: initialTop, left: initialLeft }
                }));
                
                // Initialize badge size
                setBadgeSizes(prev => ({
                    ...prev,
                    [badge.id]: badge.size || 10
                }));
                
                return [...prevBadges, badge];
            }
        });
        
        posthog?.capture('badge_selected', {
            badge_id: badge.id,
            badge_name: badge.name,
            request_id: requestId,
            total_badges: selectedBadges.length + 1
        });
    };

    const handleBadgeDragStart = (e, badgeId) => {
        e.stopPropagation(); // Prevent triggering slider drag
        
        // Don't start dragging if clicking on controls
        if (e.target.closest('.badge-remove-btn') || e.target.closest('.badge-size-control')) {
            return;
        }
        
        const event = e.touches ? e.touches[0] : e;
        const badgeElement = e.currentTarget;
        const rect = badgeElement.getBoundingClientRect();
        
        const offset = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };
        
        setDraggingBadge(badgeId);
        setDragOffset(offset);
        dragOffsetRef.current = offset;
        dragOccurredRef.current = false; // Reset flag at start of drag
        
        // Select the badge when starting to drag to show controls
        setSelectedBadgeId(badgeId);
        
        if (e.type === 'touchstart' && e.cancelable) {
            e.preventDefault();
        }
    };

    const handleBadgeDrag = (e) => {
        if (!draggingBadge || !imageComparisonDivRef.current) return;
        
        // Set flag to indicate dragging occurred
        dragOccurredRef.current = true;
        
        const event = e.touches ? e.touches[0] : e;
        const containerRect = imageComparisonDivRef.current.getBoundingClientRect();
        
        // Calculate new position relative to container
        let newLeft = event.clientX - containerRect.left - dragOffsetRef.current.x;
        let newTop = event.clientY - containerRect.top - dragOffsetRef.current.y;
        
        // Convert to percentages
        let leftPercent = (newLeft / containerRect.width) * 100;
        let topPercent = (newTop / containerRect.height) * 100;
        
        // Get badge size for boundary calculation
        const badge = selectedBadges.find(b => b.id === draggingBadge);
        const badgeSize = badgeSizes[draggingBadge] || badge?.size || 10;
        const badgeWidthPercent = badgeSize;
        // Calculate badge height as percentage to maintain aspect ratio
        const badgeHeightPercent = (badgeWidthPercent * containerRect.width) / containerRect.height;
        
        leftPercent = Math.max(0, Math.min(100 - badgeWidthPercent, leftPercent));
        topPercent = Math.max(0, Math.min(100 - badgeHeightPercent, topPercent));
        
        setBadgePositions(prev => ({
            ...prev,
            [draggingBadge]: { top: topPercent, left: leftPercent }
        }));
        
        if (e.type === 'touchmove' && e.cancelable) {
            e.preventDefault();
        }
    };

    const handleBadgeDragEnd = () => {
        setDraggingBadge(null);
        setDragOffset({ x: 0, y: 0 });
    };
    
    const handleBadgeClick = (e, badgeId) => {
        e.stopPropagation();
        
        // Don't select if we just finished dragging
        if (dragOccurredRef.current) {
            dragOccurredRef.current = false;
            return;
        }
        
        setSelectedBadgeId(badgeId);
        
        // Calculate slider position
        if (imageComparisonDivRef.current) {
            const containerRect = imageComparisonDivRef.current.getBoundingClientRect();
            const badge = selectedBadges.find(b => b.id === badgeId);
            const position = badgePositions[badgeId];
            const badgeSize = badgeSizes[badgeId] || badge?.size || 10;
            
            if (position) {
                // Calculate left edge of badge in pixels
                const leftX = containerRect.left + (position.left * containerRect.width / 100);
                // Position slider on top of badge - subtract 20px for spacing
                const topY = containerRect.top + (position.top * containerRect.height / 100) - 20;
                
                setSliderPosition({
                    top: topY,
                    left: leftX
                });
            }
        }
    };
    
    const handleBadgeResize = (badgeId, delta) => {
        setBadgeSizes(prev => {
            const currentSize = prev[badgeId] || 6;
            const newSize = Math.max(3, Math.min(20, currentSize + delta));
            return { ...prev, [badgeId]: newSize };
        });
    };
    
    const handleBadgeDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        try {
            const badgeData = e.dataTransfer.getData('badge');
            if (!badgeData) return;
            
            const badge = JSON.parse(badgeData);
            
            // Calculate drop position
            const containerRect = imageComparisonDivRef.current.getBoundingClientRect();
            const x = e.clientX - containerRect.left;
            const y = e.clientY - containerRect.top;
            
            // Convert to percentages
            const leftPercent = (x / containerRect.width) * 100;
            const topPercent = (y / containerRect.height) * 100;
            
            // Check if badge is already selected
            const isAlreadySelected = selectedBadges.some(b => b.id === badge.id);
            
            if (!isAlreadySelected) {
                // Add the badge
                setSelectedBadges(prev => [...prev, badge]);
                
                // Set position at drop location (centered on cursor)
                const badgeSize = badge.size || 10;
                setBadgePositions(prev => ({
                    ...prev,
                    [badge.id]: { 
                        top: Math.max(0, Math.min(100 - badgeSize, topPercent - badgeSize/2)), 
                        left: Math.max(0, Math.min(100 - badgeSize, leftPercent - badgeSize/2))
                    }
                }));
                
                // Initialize badge size
                setBadgeSizes(prev => ({
                    ...prev,
                    [badge.id]: badgeSize
                }));
                
                // Select the newly dropped badge to show controls
                setSelectedBadgeId(badge.id);
            }
        } catch (error) {
            console.error('Error handling badge drop:', error);
        }
    };

    // Set page load timestamp for tracking
    useEffect(() => {
        window.sharePageLoadStart = Date.now();
        
        posthog?.capture('share_page_loaded', {
            request_id: requestId,
            referrer: document.referrer,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString()
        });
        
        return () => {
            // Clean up timestamp when component unmounts
            delete window.sharePageLoadStart;
        };
    }, [requestId, posthog]);

    const handleTryAgain = async () => {
        posthog?.capture('try_again_clicked', {
            request_id: requestId,
            strava_activity_id: requestData?.StravaId,
            source: 'share_page_feedback_section'
        });
        
        // Check if user is authenticated
        if (!currentUser) {
            showCustomDialog(
                'info',
                'Sign In Required',
                'Please sign in to regenerate this map.',
                'ti-info-circle'
            );
            return;
        }
        
        // Show loading state
        setIsSubmittingFeedback(true);
        
        try {
            const regenerateMapFunc = httpsCallable(functions, 'regenerateMap');
            const result = await regenerateMapFunc({ originalRequestId: requestId });
            
            if (result.data.success) {
                posthog?.capture('map_regenerated', {
                    original_request_id: requestId,
                    new_request_id: result.data.newRequestId,
                    regeneration_number: result.data.regenerationNumber,
                    strava_activity_id: requestData?.StravaId
                });
                
                // Navigate to the new map page
                navigate(`/map/${result.data.newRequestId}`);
            }
        } catch (error) {
            console.error('Error regenerating map:', error);
            
            let errorMessage = 'Failed to regenerate map. Please try again.';
            if (error.code === 'resource-exhausted') {
                errorMessage = 'You have reached the maximum number of regenerations (3) for this map.';
            } else if (error.code === 'permission-denied') {
                errorMessage = 'You can only regenerate your own maps.';
            } else if (error.code === 'failed-precondition') {
                errorMessage = 'Map must be completed before regenerating.';
            }
            
            showCustomDialog(
                'error',
                'Regeneration Failed',
                errorMessage,
                'ti-alert-circle'
            );
            
            posthog?.capture('map_regeneration_failed', {
                request_id: requestId,
                error: error.message,
                error_code: error.code
            });
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    if (isLoading) {
        return (
            <div className="container share-page">
                <div className="share-loading">
                    <p>Loading your mythic map...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container share-page">
            <div className="share-header-row">
                <h1>{t('landing.makeItMythic', 'Live the Myth')}</h1>
            </div>

            {/* Show different header content based on authentication status */}
            {currentUser ? (
                <div className="header-cards-container" style={{ marginBottom: '30px' }}>
                    <PromoCard 
                        user={currentUser}
                        posthog={posthog}
                        variant="header"
                        onRewardClaimed={() => loadMonthlyUsage(currentUser)}
                        style={{ flex: 1 }}
                    />
                    <CreditsCounter 
                        isLoadingUsage={isLoadingUsage}
                        monthlyUsage={monthlyUsage}
                        posthog={posthog}
                        page="share"
                        source="credits_card"
                    />
                </div>
            ) : (
                <div className="join-mythic-card" style={{ marginBottom: '30px' }}>
                    <div className="join-mythic-content">
                        <span className="join-mythic-text">Join Mythic Maps</span>
                        <button onClick={() => {
                            posthog?.capture('join_mythic_maps_clicked', {
                                source: 'share_page_header',
                                request_id: requestId,
                                context: 'unauthenticated_user'
                            });
                            handleStravaConnect();
                        }} className="strava-connect-button-wrapper">
                            <img src={stravaConnectButton} alt="Connect with Strava" className="strava-connect-btn-img" />
                        </button>
                    </div>
                </div>
            )}

            {/* Show error state when there's an error */}
            {error && (
                <div className="generation-progress">
                    <div className="progress-content">
                        <div className="progress-spinner error">
                            <i className="ti ti-alert-circle" style={{ fontSize: '48px', color: '#dc2626' }}></i>
                        </div>
                        <h3>{t('share.generationFailed', 'Generation Failed')}</h3>
                        <p className="progress-message error-message">{error}</p>
                    </div>
                </div>
            )}

            {/* Show generation progress when images aren't ready yet and no error */}
            {requestData && !error && !imageDisplayVisible && (
                <div className="generation-progress">
                    <div className="progress-content">
                        <div className="progress-spinner">
                            <div className="spinner"></div>
                        </div>
                        <h3>{t('share.creatingHeader', 'Creating Your Mythic Map')}</h3>
                        <p className="progress-message">
                            {getStatusMessage(requestData.status, currentAiMessage, t)}
                        </p>
                        
                        {/* Progress Bar */}
                        {progressPercentage > 0 && (
                            <div className="progress-bar-container">
                                <div className="progress-bar">
                                    <div 
                                        className="progress-bar-fill" 
                                        style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                                    ></div>
                                </div>
                                <div className="progress-info">
                                    <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
                                    {/* Only show ETA during AI processing when estimates are meaningful */}
                                    {requestData?.status === 'openai_processing' && estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
                                        <span className="progress-eta">
                                            {t('share.eta', 'ETA')}: {formatDuration(estimatedTimeRemaining)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Show available badges immediately while generation is in progress */}
                        {(isAdmin || badgesVisibleToAll) && currentUser && requestData?.userId === currentUser.uid && availableBadges.length > 0 && (
                            <div style={{ marginTop: '20px' }} className="early-badges-section">
                                <MapBadges 
                                    onBadgeSelect={handleBadgeSelect}
                                    selectedBadges={selectedBadges}
                                    availableBadges={availableBadges}
                                    enableToggle={false}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {requestData && !error && imageDisplayVisible && (
                <>
                    {/* Activity Details Section */}
                    <div className="activity-section">
                        <h2>{t('share.originalActivity', 'Your original activity')}</h2>
                        <p className="section-subtitle">
                            {regenerationInfo.isRegeneration 
                                ? `Regeneration ${regenerationInfo.regenerationNumber} of 3` 
                                : t('share.shareTagline', "Let's share the Myth")}
                        </p>

                        <div className="activity-card">
                            <div className="activity-thumbnail">
                                {originalImageSrc ? (
                                    <img src={originalImageSrc} alt="Original activity map" />
                                ) : (
                                    <div className="thumbnail-placeholder">Loading map...</div>
                                )}
                            </div>

                            <div className="activity-details">
                                <div className="activity-title">
                                    <div className="activity-title-left">
                                        {/* Icon removed - ActivityIcon now returns null */}
                                        <h3>{requestData.stravaStats?.activityName || 'Untitled Activity'}</h3>
                                    </div>
                                    {requestData.StravaId && (
                                        <div className="activity-title-right">
                                            <a 
                                                href={`https://www.strava.com/activities/${requestData.StravaId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="strava-view-link"
                                                onClick={() => {
                                                    posthog?.capture('view_on_strava_clicked', {
                                                        source: 'share_page_activity_card',
                                                        strava_activity_id: requestData.StravaId,
                                                        request_id: requestId
                                                    });
                                                }}
                                                >
                                                 {t('viewOnStrava')}
                                                <i className="ti ti-external-link" style={{ marginLeft: '4px', fontSize: '14px' }}></i>
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {showWatermarkButton && !isLegacyMap() && (hasExistingWatermark || activityHasClaimedCredit) && (
                                    <div className="watermark-credit-status">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        <span>{t('share.shareStrava', 'Share to Strava')} • {t('share.creditClaimed', 'Credit claimed')}</span>
                                    </div>
                                )}

                                <div className="activity-stats">
                                    <div className="stat">
                                         <span className="stat-label">{t('history.distance', 'Distance')}</span>
                                        <span className="stat-value">
                                            {requestData.stravaStats?.distance ?
                                                `${(requestData.stravaStats.distance / 1000).toFixed(1)} km` :
                                                'N/A'
                                            }
                                        </span>
                                    </div>
                                    <div className="stat">
                                         <span className="stat-label">{t('share.elevationGain', 'Elevation gain')}</span>
                                        <span className="stat-value">
                                            {requestData.stravaStats?.elevationGain ?
                                                `${Math.round(requestData.stravaStats.elevationGain)} m` :
                                                'N/A'
                                            }
                                        </span>
                                    </div>
                                    <div className="stat">
                                         <span className="stat-label">{t('history.time', 'Time')}</span>
                                        <span className="stat-value">
                                            {formatDuration(requestData.stravaStats?.movingTime)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Image Comparison Section - Separate from activity */}
                    {imageDisplayVisible && (
                        <div className="comparison-section">
                            <div className="image-comparison-wrapper">
                                <div
                                    className={`image-comparison-container ${selectedBadges.length > 0 ? 'has-badges' : ''}`}
                                    ref={imageComparisonDivRef}
                                    onMouseDown={startDrag}
                                    onTouchStart={startDrag}
                                    onClick={(e) => {
                                        // Only deselect if not clicking on a badge
                                        if (!e.target.closest('.map-badge-overlay')) {
                                            setSelectedBadgeId(null);
                                        }
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleBadgeDrop}
                                    style={{ position: 'relative' }}
                                >
                                    {originalImageSrc && <img src={originalImageSrc} alt="Original Map" />}
                                    {generatedImageSrc && (
                                        <>
                                            <img
                                                id="generatedImageDisplayShared"
                                                src={generatedImageSrc}
                                                alt="Generated AI Map"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                            {/* Badge overlay container - Admin or when visible to all */}
                                            {(isAdmin || badgesVisibleToAll) && currentUser && requestData?.userId === currentUser.uid && (
                                                <div className="map-badge-overlay-container">
                                                    {selectedBadges.map((badge) => {
                                                    const position = badgePositions[badge.id] || { top: 3, left: 3 };
                                                    
                                                    return (
                                                        <div
                                                            key={badge.id}
                                                            className={`map-badge-overlay ${draggingBadge === badge.id ? 'dragging' : ''} ${selectedBadgeId === badge.id ? 'selected' : ''}`}
                                                            style={{
                                                                top: `${position.top}%`,
                                                                left: `${position.left}%`,
                                                                cursor: draggingBadge === badge.id ? 'grabbing' : 'grab',
                                                                pointerEvents: 'auto',
                                                                zIndex: draggingBadge === badge.id ? 100 : 10,
                                                                width: `${badgeSizes[badge.id] || badge.size || 10}%`,
                                                                aspectRatio: '1 / 1'
                                                            }}
                                                            onClick={(e) => handleBadgeClick(e, badge.id)}
                                                            onMouseDown={(e) => handleBadgeDragStart(e, badge.id)}
                                                            onTouchStart={(e) => handleBadgeDragStart(e, badge.id)}
                                                            onMouseEnter={() => {
                                                                setHoveredBadgeId(badge.id);
                                                                setSelectedBadgeId(badge.id);
                                                            }}
                                                        >
                                                            <img 
                                                                src={badge.imageUrl} 
                                                                alt={badge.name}
                                                                style={{ 
                                                                    width: '100%', 
                                                                    height: '100%', 
                                                                    objectFit: 'contain',
                                                                    pointerEvents: 'none'
                                                                }}
                                                            />
                                                            {selectedBadgeId === badge.id && (
                                                                <>
                                                                    {/* X button to remove */}
                                                                    <button
                                                                        className="badge-remove-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleBadgeSelect(badge);
                                                                        }}
                                                                        title="Remove badge"
                                                                    >
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                                                        </svg>
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {selectedBadges.length === 0 && (
                                        <div className="slider-line" style={{ top: `${sliderValue}%` }} />
                                    )}
                                </div>
                            </div>
                            
                            {/* Badge size slider - rendered outside map container */}
                            {selectedBadgeId && (isAdmin || badgesVisibleToAll) && (
                                <div 
                                    className="badge-size-control-fixed"
                                    style={{
                                        position: 'fixed',
                                        top: `${sliderPosition.top}px`,
                                        left: `${sliderPosition.left}px`,
                                        zIndex: 1000
                                    }}
                                >
                                    <input
                                        type="range"
                                        min="10"
                                        max="30"
                                        value={badgeSizes[selectedBadgeId] || 10}
                                        onChange={(e) => {
                                            setBadgeSizes(prev => ({
                                                ...prev,
                                                [selectedBadgeId]: parseFloat(e.target.value)
                                            }));
                                        }}
                                        className="badge-size-slider"
                                        title="Resize badge"
                                    />
                                </div>
                            )}

                            {/* Badge Selection Section - Admin or when visible to all */}
                            {(isAdmin || badgesVisibleToAll) && currentUser && requestData?.userId === currentUser.uid && (
                                <>
                                    <MapBadges 
                                        onBadgeSelect={handleBadgeSelect}
                                        selectedBadges={selectedBadges}
                                        availableBadges={availableBadges}
                                        showInstructions={showBadgeInstructions}
                                        enableToggle={true}
                                        onToggleInstructions={() => setShowBadgeInstructions(true)}
                                    />
                                </>
                            )}

                            {isAdmin && contourImageUrl && (
                                <div className="contour-preview-card">
                                    <h3 className="contour-preview-title">Contour Preprocessing Preview</h3>
                                    <div className="contour-card-content">
                                        <div
                                            className="contour-image-wrapper"
                                            onMouseEnter={() => {
                                                if (contourImageUrl) {
                                                    setIsContourOverlayOpen(true);
                                                }
                                            }}
                                            onMouseLeave={(event) => {
                                                const nextTarget = event.relatedTarget;
                                                if (nextTarget && nextTarget.closest && nextTarget.closest('.contour-image-overlay')) {
                                                    return;
                                                }
                                                setIsContourOverlayOpen(false);
                                            }}
                                        >
                                            <img
                                                src={contourImageUrl}
                                                alt="Contour preprocessing output"
                                                className="contour-image-thumb"
                                            />
                                            <span className="contour-hover-hint">Hover to enlarge</span>
                                        </div>
                                        {contourPrompt && (
                                            <div className="contour-prompt">
                                                <h4 className="contour-prompt-title">Flux Prompt</h4>
                                                <pre className="contour-prompt-text">{contourPrompt}</pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {contourImageUrl && (
                                <div
                                    className={`contour-image-overlay ${isContourOverlayOpen ? 'is-open' : ''}`}
                                    onClick={() => setIsContourOverlayOpen(false)}
                                    onMouseLeave={(event) => {
                                        const nextTarget = event.relatedTarget;
                                        if (nextTarget && nextTarget.closest && nextTarget.closest('.contour-image-wrapper')) {
                                            return;
                                        }
                                        setIsContourOverlayOpen(false);
                                    }}
                                    role="button"
                                    tabIndex={-1}
                                >
                                    <img
                                        src={contourImageUrl}
                                        alt="Contour preprocessing output enlarged"
                                        className="contour-image-full"
                                    />
                                </div>
                            )}

                            {/* Social Sharing Section - positioned just below main image */}
                            <div className="social-sharing-section" style={{ paddingBottom: '16px' }}>
                                <h3 style={{ textAlign: 'center', fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>{t('share.shareHeader', 'Share the Myth')}</h3>

                                <div className="share-buttons">
                                    <button
                                        onClick={handleDownloadAiImage}
                                        disabled={isDownloading}
                                        className="download-button"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        {isDownloading ? t('share.downloading', 'Downloading...') : t('share.download', 'Download')}
                                    </button>

                                    {/* TEMPORARY: iOS Download button for testing on PC */}
                                    {isAdmin && (
                                        <button
                                        onClick={async () => {
                                            // Get download button reference outside try block
                                            const downloadButton = document.querySelector('.ios-test-button');
                                            const originalButtonText = downloadButton ? downloadButton.textContent : '';
                                            
                                            try {
                                                // Show loading state for download button
                                                if (downloadButton) {
                                                    downloadButton.textContent = 'Preparing iOS view...';
                                                    downloadButton.disabled = true;
                                                }

                                                let imageUrlToUse = generatedImageSrc;

                                                // If badges are present, create composite image
                                                if (selectedBadges.length > 0) {
                                                    try {
                                                        // Show upload progress immediately
                                                        setIsUploadingComposite(true);
                                                        
                                                        const blob = await createMapWithBadges();
                                                        
                                                        // Upload composite image to Firebase Storage
                                                        console.log('Uploading composite image to Firebase Storage...');
                                                        const compositeUrl = await uploadCompositeImage(blob);
                                                        console.log('Composite image uploaded:', compositeUrl);
                                                        
                                                        // Use the Firebase Storage URL
                                                        imageUrlToUse = compositeUrl;
                                                    } catch (compositeError) {
                                                        console.error('Failed to create composite image with badges:', compositeError);
                                                        alert('Unable to include badges in the download. The image will be downloaded without badges.');
                                                        imageUrlToUse = generatedImageSrc;
                                                    } finally {
                                                        setIsUploadingComposite(false);
                                                    }
                                                }

                                                // For iOS devices: Redirect to special iOS download page
                                                console.log('Image URL to use:', imageUrlToUse);
                                                let iosUrl = `/ios-download/${requestId}?imageUrl=${encodeURIComponent(imageUrlToUse)}`;

                                                // Only add Strava ID if it exists
                                                if (requestData && requestData.StravaId) {
                                                    iosUrl += `&stravaId=${requestData.StravaId}`;
                                                }

                                                console.log('Opening iOS URL:', iosUrl);
                                                const newWindow = window.open(iosUrl, '_blank');
                                                
                                                // Check if popup was blocked
                                                if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
                                                    console.warn('Popup blocked, using location redirect');
                                                    window.location.href = iosUrl;
                                                }
                                                
                                                // Reset button state after successful operation
                                                if (downloadButton) {
                                                    downloadButton.textContent = originalButtonText;
                                                    downloadButton.disabled = false;
                                                }
                                            } catch (error) {
                                                console.error('Error preparing iOS download:', error);
                                                alert('Failed to prepare iOS view. Please try again.');
                                                if (downloadButton) {
                                                    downloadButton.textContent = originalButtonText;
                                                    downloadButton.disabled = false;
                                                }
                                            }
                                        }}
                                        className="download-button ios-test-button"
                                        style={{ backgroundColor: '#007AFF' }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                                            <line x1="12" y1="18" x2="12.01" y2="18"></line>
                                        </svg>
                                        Test iOS View
                                    </button>
                                    )}

                                    {showWatermarkButton && !isLegacyMap() && !stravaShareInitiated && currentUser && requestData?.userId === currentUser.uid && (
                                        <button
                                            onClick={handleStravaDownload}
                                            className="strava-action-button"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                                            </svg>
                                             {hasExistingWatermark || activityHasClaimedCredit ? t('share.shareStrava', 'Share to Strava') : t('share.shareStravaAndGet', 'Share to Strava & get 1 free credit')}
                                        </button>
                                    )}


                                    {showWatermarkButton && !isLegacyMap() && stravaShareInitiated && currentUser && requestData?.StravaId && requestData?.userId === currentUser.uid && (
                                        <button
                                            onClick={runWatermarkCheck}
                                            disabled={isCheckingWatermark || watermarkCheckStatus === 'checking' || activityHasClaimedCredit || hasExistingWatermark}
                                            className={`watermark-check-button ${(activityHasClaimedCredit || hasExistingWatermark) ? 'claimed' : ''}`}
                                        >
                                            {isCheckingWatermark || watermarkCheckStatus === 'checking' ? (
                                                <>
                                                    <div className="loading-spinner-small"></div>
                                                    {t('share.checking', 'Checking...')}
                                                </>
                                            ) : (activityHasClaimedCredit || hasExistingWatermark) ? (
                                                <>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                    {t('share.creditClaimed', 'Credit claimed')}
                                                </>
                                            ) : (
                                                 t('share.claimCredit', 'Claim your credit')
                                            )}
                                        </button>
                                    )}

                                    <button
                                        onClick={() => setShowSocialShareModal(true)}
                                        className="download-button"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="18" cy="5" r="3"></circle>
                                            <circle cx="6" cy="12" r="3"></circle>
                                            <circle cx="18" cy="19" r="3"></circle>
                                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                                        </svg>
                                         {t('share.socialShare', 'Social Share')}
                                    </button>

                                    <button
                                        onClick={handleCopyLink}
                                        className={`download-button ${copySuccess ? 'success' : ''}`}
                                        title="Copy link to clipboard"
                                    >
                                        {copySuccess ? (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                                 {t('share.copied', 'Copied!')}
                                            </>
                                        ) : (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                                 {t('share.copyLink', 'Copy Link')}
                                            </>
                                        )}
                                    </button>
                                </div>

                                {shareInstructions && (
                                    <div className="share-instructions">
                                        {typeof shareInstructions === 'string' ? (
                                            <p>{shareInstructions}</p>
                                        ) : (
                                            shareInstructions
                                        )}
                                    </div>
                                )}
                            </div>

                        </div>
                    )}

                    {/* Share Actions Section */}
                    <div className="share-actions-section">
                        
                        {/* Feedback Section - Show only if user owns the activity */}
                        {currentUser && requestData?.userId === currentUser.uid && (
                            <div className="feedback-section">
                                <p>{t('share.howIsYourMap', 'How is your map?')}</p>
                                <div className="feedback-buttons">
                                    <button
                                        onClick={() => handleFeedback('like')}
                                        disabled={isSubmittingFeedback}
                                        className={`feedback-button ${userFeedback === 'like' ? 'active like' : ''} ${userFeedback && userFeedback !== 'like' ? 'inactive' : ''} ${isSubmittingFeedback && userFeedback === 'like' ? 'submitting' : ''}`}
                                    >
                                        {isSubmittingFeedback && userFeedback === 'like' ? (
                                            <div className="loading-spinner-small"></div>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                                            </svg>
                                        )}
                                         {t('share.like', 'Like')}
                                    </button>
                                    
                                    <button
                                        onClick={() => handleFeedback('dislike')}
                                        disabled={isSubmittingFeedback}
                                        className={`feedback-button ${userFeedback === 'dislike' ? 'active dislike' : ''} ${userFeedback && userFeedback !== 'dislike' ? 'inactive' : ''} ${isSubmittingFeedback && userFeedback === 'dislike' ? 'submitting' : ''}`}
                                    >
                                        {isSubmittingFeedback && userFeedback === 'dislike' ? (
                                            <div className="loading-spinner-small"></div>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
                                            </svg>
                                        )}
                                         {t('share.dislike', 'Dislike')}
                                    </button>

                                    <button
                                        onClick={handleTryAgain}
                                        className={`feedback-button try-again ${regenerationInfo.remainingRegenerations === 0 ? 'disabled' : ''}`}
                                        disabled={isSubmittingFeedback || regenerationInfo.remainingRegenerations === 0}
                                        title={regenerationInfo.remainingRegenerations === 0 ? 'Maximum regenerations reached' : `${regenerationInfo.remainingRegenerations} regenerations remaining`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill="none">
                                            <path d="M1 8C1 8 1.12132 7.15076 4.63604 3.63604C8.15076 0.12132 13.8492 0.12132 17.364 3.63604C18.6092 4.88131 19.4133 6.40072 19.7762 8M1 8V2M1 8H7M21 12C21 12 20.8787 12.8492 17.364 16.364C13.8492 19.8787 8.15076 19.8787 4.63604 16.364C3.39076 15.1187 2.58669 13.5993 2.22383 12M21 12V18M21 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                         {t('share.tryAgain', 'Try again for free')}
                                        {regenerationInfo.remainingRegenerations > 0 && regenerationInfo.remainingRegenerations < 3 && (
                                            <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                                                ({regenerationInfo.remainingRegenerations} left)
                                            </span>
                                        )}
                                    </button>
                                    
                                </div>
                            </div>
                        )}
                        
                        {/* Gallery Visibility Toggle - Show only if user owns the map AND is admin */}
                        {currentUser && requestData?.userId === currentUser.uid && isAdmin && (
                            <div className="gallery-toggle-section">
                                <div className="gallery-toggle-content">
                                    <div className="gallery-toggle-text">
                                         <h4>{t('share.galleryVisibility', 'Gallery Visibility')}</h4>
                                        <p>
                                             {isPublic ? t('share.galleryPublic', 'Your map is visible in the public gallery') : t('share.galleryMakePublic', 'Make your map visible in the public gallery')}
                                        </p>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={isPublic}
                                            onChange={handleToggleVisibility}
                                            disabled={isTogglingVisibility}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Admin-only button to regenerate with new prompt */}
                        {isAdmin && requestData?.status === 'openai_processing_complete' && (
                            <div className="admin-actions" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <Link
                                    to={`/generate/request/${requestId}`}
                                    className="admin-regenerate-button"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 20px',
                                        backgroundColor: '#4338ca',
                                        color: 'white',
                                        borderRadius: '8px',
                                        textDecoration: 'none',
                                        transition: 'background-color 0.2s',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onClick={() => posthog?.capture('admin_regenerate_clicked', { 
                                        request_id: requestId,
                                        from_page: 'share'
                                    })}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M23 4v6h-6"></path>
                                        <path d="M1 20v-6h6"></path>
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                                    </svg>
                                     {t('share.useWithPrompt', 'Use Map with New Prompt (Admin)')}
                                </Link>
                            </div>
                        )}
                    </div>
                </>
            )}

            {showSocialShareModal && (
                <div className="dialog-overlay" onClick={() => setShowSocialShareModal(false)}>
                    <div className="dialog-container social-share-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="social-share-header">
                            <h3>Share on Social Media</h3>
                            <button onClick={() => setShowSocialShareModal(false)} className="modal-close-button">&times;</button>
                        </div>
                        <div className="social-share-body">
                            <div className="social-grid">
                                <button
                                    onClick={handleStravaDownload}
                                    className="icon-button strava"
                                    title="Download for Strava"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleShare('instagram')}
                                    className="icon-button instagram"
                                    title="Share on Instagram"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleShare('reddit')}
                                    className="icon-button reddit"
                                    title="Share on Reddit"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleShare('facebook')}
                                    className="icon-button facebook"
                                    title="Share on Facebook"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleShare('twitter')}
                                    className="icon-button twitter"
                                    title="Share on X"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleShare('tiktok')}
                                    className="icon-button tiktok"
                                    title="Share on TikTok"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                                    </svg>
                                </button>
                            </div>
                            {shareInstructions && (
                                <div className="share-instructions" style={{marginTop: '20px'}}>
                                    {typeof shareInstructions === 'string' ? (
                                        <p>{shareInstructions}</p>
                                    ) : (
                                        shareInstructions
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Custom Dialog */}
            {showDialog && (
                <div className="dialog-overlay" onClick={() => {
                    posthog?.capture('dialog_closed', {
                        dialog_type: dialogContent.type,
                        dialog_title: dialogContent.title,
                        request_id: requestId,
                        action: 'overlay_click',
                        page: 'share'
                    });
                    setShowDialog(false);
                }}>
                    <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
                        <div className={`dialog-content ${dialogContent.type}`}>
                            <div className="dialog-header">
                                {dialogContent.icon && (
                                    <i className={`${dialogContent.icon} dialog-icon`}></i>
                                )}
                                <h3 className="dialog-title">{dialogContent.title}</h3>
                            </div>
                            <div className="dialog-body">
                                <div className="dialog-message">{dialogContent.message}</div>
                            </div>
                            <div className="dialog-actions">
                                <button 
                                    onClick={() => {
                                        posthog?.capture('dialog_closed', {
                                            dialog_type: dialogContent.type,
                                            dialog_title: dialogContent.title,
                                            request_id: requestId,
                                            action: 'ok_button',
                                            page: 'share'
                                        });
                                        setShowDialog(false);
                                    }}
                                    className="dialog-button primary"
                                >
                                    {t('common.ok', 'OK')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* iOS-style Upload Progress Modal */}
            {isUploadingComposite && (
                <div className="ios-upload-overlay">
                    <div className="ios-upload-modal">
                        <div className="ios-upload-content">
                            <div className="ios-upload-spinner">
                                <svg className="ios-spinner" viewBox="0 0 50 50">
                                    <circle 
                                        className="ios-spinner-path" 
                                        cx="25" 
                                        cy="25" 
                                        r="20" 
                                        fill="none" 
                                        strokeWidth="3"
                                    />
                                </svg>
                            </div>
                            <h3 className="ios-upload-title">Preparing Image</h3>
                            <p className="ios-upload-message">
                                Adding badges to your map...
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SharePage; 
